import React, { useMemo } from "react"
import { Platform, View } from "react-native"
import Svg, { Path } from "react-native-svg"

export type LightweightPieSlice = {
  value: number
  color: string
  key?: string
}

type Props = {
  data: LightweightPieSlice[]
  size?: number
  innerRadius?: number
  centerContent?: React.ReactNode
  onSliceHover?: (index: number) => void
  onSliceLeave?: () => void
}

type CartesianPoint = {
  x: number
  y: number
}

const clamp = (n: number, min: number, max: number): number => {
  return Math.min(Math.max(n, min), max)
}

const polarToCartesian = (
  cx: number,
  cy: number,
  radius: number,
  angleInDegrees: number
): CartesianPoint => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180
  return {
    x: cx + (radius * Math.cos(angleInRadians)),
    y: cy + (radius * Math.sin(angleInRadians)),
  }
}

const buildSlicePath = (
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number
): string => {
  const EPSILON = 0.0001
  const sweep = endAngle - startAngle
  const absSweep = Math.abs(sweep)
  const normalizedSweep = absSweep % 360
  const isFullCircle = (
    absSweep >= (360 - EPSILON) ||
    normalizedSweep <= EPSILON
  )

  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle)
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle)
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0

  if (isFullCircle) {
    const outerMid = polarToCartesian(cx, cy, outerRadius, startAngle + 180)

    if (innerRadius <= 0) {
      return [
        `M ${cx} ${cy}`,
        `L ${outerStart.x} ${outerStart.y}`,
        `A ${outerRadius} ${outerRadius} 0 0 1 ${outerMid.x} ${outerMid.y}`,
        `A ${outerRadius} ${outerRadius} 0 0 1 ${outerStart.x} ${outerStart.y}`,
        "Z",
      ].join(" ")
    }

    const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle)
    const innerMid = polarToCartesian(cx, cy, innerRadius, startAngle + 180)

    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerRadius} ${outerRadius} 0 0 1 ${outerMid.x} ${outerMid.y}`,
      `A ${outerRadius} ${outerRadius} 0 0 1 ${outerStart.x} ${outerStart.y}`,
      `L ${innerStart.x} ${innerStart.y}`,
      `A ${innerRadius} ${innerRadius} 0 0 0 ${innerMid.x} ${innerMid.y}`,
      `A ${innerRadius} ${innerRadius} 0 0 0 ${innerStart.x} ${innerStart.y}`,
      "Z",
    ].join(" ")
  }

  if (innerRadius <= 0) {
    return [
      `M ${cx} ${cy}`,
      `L ${outerStart.x} ${outerStart.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
      "Z",
    ].join(" ")
  }

  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle)
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle)

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ")
}

const LightweightPieChart: React.FC<Props> = ({
  data,
  size = 220,
  innerRadius = 0,
  centerContent,
  onSliceHover,
  onSliceLeave,
}: Props) => {
  const radius = size / 2
  const safeInnerRadius = clamp(innerRadius, 0, radius - 1)

  const safeData = useMemo(() => (
    Array.isArray(data)
      ? data
        .map((slice, originalIndex) => {
          const value = Number(slice?.value || 0)
          if (!Number.isFinite(value) || value <= 0) return null
          return {
            ...slice,
            value,
            originalIndex,
          }
        })
        .filter((slice) => slice !== null && Number(slice?.value) > 0)
      : []
  ), [data])

  const total = useMemo(
    () => safeData.reduce((sum, slice) => sum + slice.value, 0),
    [safeData]
  )

  const sliceDefs = useMemo(() => {
    if (!total || safeData.length === 0) return []

    let currentAngle = 0
    return safeData.map((slice) => {
      const value = Number(slice.value || 0)
      const sweep = total > 0 ? (value / total) * 360 : 0
      const startAngle = currentAngle
      const endAngle = currentAngle + sweep
      currentAngle = endAngle
      return {
        ...slice,
        path: buildSlicePath(radius, radius, radius, safeInnerRadius, startAngle, endAngle)
      }
    })
  }, [radius, safeData, safeInnerRadius, total])

  return (
    <View style={{ width: size, height: size, alignItems: "center", justifyContent: "center" }}>
      <Svg width={size} height={size}>
        {sliceDefs.map((slice) => (
          <Path
            key={slice.key || `${slice.color}-${slice.originalIndex}`}
            d={slice.path}
            fill={slice.color}
            {...(
              Platform.OS === "web" ? {
                onMouseEnter: () => onSliceHover?.(slice.originalIndex),
                onMouseLeave: () => onSliceLeave?.(),
                onPressIn: () => onSliceHover?.(slice.originalIndex),
                onPressOut: () => onSliceLeave?.(),
              } : {
                onPressIn: () => onSliceHover?.(slice.originalIndex),
                onPressOut: () => onSliceLeave?.(),
              }
            )}
          />
        ))}
      </Svg>
      {centerContent ? (
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {centerContent}
        </View>
      ) : null}
    </View>
  )
}

export default LightweightPieChart
